import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useHistory } from 'react-router-dom';

// Data
import { ICreateAssessmentContainer, ICreateAssessmentFields } from './create-assessment.interface';
import { validationSchema } from './assessment-info-form.validation';
import { getAuthUser } from '../../../lib/services/auth.service';

// Components
import { DEFAULT_ASSESSMENT_DESCRIPTOR, SEASON_OPTIONS } from './create-assessment.constants';
import { AssessmentInfoForm } from './assessment-info-form.component';
import { RoleAssignmentDialog } from './role-assignment-dialog.component';

const CreateAssessmentContainer: React.FC<ICreateAssessmentContainer> = ({
  createAssessmentMutation,
  company,
  members = [],
  currentAssessmentId,
  copyRolesForMembers
}) => {
  const history = useHistory();
  const user = getAuthUser();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [pendingAssessmentData, setPendingAssessmentData] = useState<any>(null);
  const [assessmentName, setAssessmentName] = useState('');
  const [isCreatingAssessment, setIsCreatingAssessment] = useState(false);

  const INITIAL_FORM_VALUES: ICreateAssessmentFields = {
    year: new Date().getFullYear(),
    season: SEASON_OPTIONS[Math.floor(new Date().getMonth() / 3)],
    descriptor: DEFAULT_ASSESSMENT_DESCRIPTOR,
    additionalContext: "",
    infoLink: "",
    concludeEmailTemplate: "",
    evaluationGroupName: company?.evaluationGroupName,
    introLink: company?.introLink
  };

  async function handleSubmit({additionalContext, descriptor, season, year, infoLink, evaluationGroupName, introLink}: ICreateAssessmentFields, { setSubmitting }: FormikHelpers<ICreateAssessmentFields>) {
    setSubmitError(null);

    const name = [year, season.value, descriptor.value, additionalContext].filter(v => v).join(" ");
    setAssessmentName(name);

    const assessmentData = {
      name,
      companyId: user?.settings.activeCompanyId,
      infoLink,
      evaluationGroupName,
      introLink
    };

    setPendingAssessmentData(assessmentData);
    setSubmitting(false);

    // Check if there are any eligible members with assessment-level roles
    const eligibleMembers = currentAssessmentId && members ? members.filter(member => {
      const assessmentRoles = member.rolesByAssessment?.find(
        r => r.assessmentId === currentAssessmentId
      );
      return assessmentRoles && assessmentRoles.roles.length > 0;
    }) : [];

    // Show role assignment dialog only if there are eligible members
    if (eligibleMembers.length > 0) {
      setShowRoleDialog(true);
    } else {
      // No eligible members, create assessment directly
      await createAssessmentWithRoles(assessmentData, []);
    }
  }

  async function createAssessmentWithRoles(assessmentData: any, selectedUserIds: string[]) {
    // Prevent double creation
    if (isCreatingAssessment) {
      console.log('Assessment creation already in progress, skipping...');
      return;
    }

    setIsCreatingAssessment(true);

    try {
      const result = await createAssessmentMutation(assessmentData);

      if (result.error) {
        setSubmitError(result.error.message);
        setIsCreatingAssessment(false);
      } else if (result.data) {
        const newAssessmentId = result.data.createAssessment._id;
        console.log('Assessment created with ID:', newAssessmentId);

        // Copy roles for selected members if any
        if (selectedUserIds.length > 0 && copyRolesForMembers && currentAssessmentId) {
          console.log(`Copying roles for ${selectedUserIds.length} members from assessment ${currentAssessmentId} to ${newAssessmentId}`);
          try {
            await copyRolesForMembers({
              fromAssessmentId: currentAssessmentId,
              toAssessmentId: newAssessmentId,
              memberIds: selectedUserIds
            });
            console.log('Role copying completed');
          } catch (roleError) {
            console.error('Failed to copy roles:', roleError);
            // Don't fail the entire operation if role copying fails
            // User can still manually assign roles later
          }
        }

        history.push('/');
      }
    } catch (error) {
      console.error('Failed to create assessment:', error);
      setSubmitError((error as Error).message || 'Failed to create assessment');
      setIsCreatingAssessment(false);
    }
  }

  const handleRoleDialogConfirm = (selectedUserIds: string[]) => {
    if (pendingAssessmentData) {
      setShowRoleDialog(false); // Close the dialog
      const dataToCreate = pendingAssessmentData;
      setPendingAssessmentData(null); // Clear pending data to prevent re-use
      createAssessmentWithRoles(dataToCreate, selectedUserIds);
    }
  };

  const handleRoleDialogClose = () => {
    setShowRoleDialog(false);
    // When user cancels the dialog, create assessment without copying roles
    if (pendingAssessmentData && !isCreatingAssessment) {
      createAssessmentWithRoles(pendingAssessmentData, []);
    }
    // Clear pending data after handling
    setPendingAssessmentData(null);
  };

  return (
    <>
      <Formik
        initialValues={INITIAL_FORM_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {form => <AssessmentInfoForm form={form} submitError={submitError || undefined} company={company} />}
      </Formik>

      <RoleAssignmentDialog
        open={showRoleDialog}
        onClose={handleRoleDialogClose}
        onConfirm={handleRoleDialogConfirm}
        currentAssessmentId={currentAssessmentId}
        members={members}
        assessmentName={assessmentName}
      />
    </>
  );
};

export default CreateAssessmentContainer;
